import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { API_URLS, API_CONFIG } from '../../config/api';
import { 
  ArrowLeft, 
  ExternalLink, 
  Building2, 
  Globe, 
  Calendar,
  Tag,
  CheckCircle2
} from 'lucide-react';

export default function ReferenceDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [reference, setReference] = useState(null);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedReferences, setRelatedReferences] = useState([]);

  const BASE_URL = API_CONFIG.BASE_URL;
  
  const normalizeUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (url.startsWith('/')) return `${BASE_URL}${url}`;
    return `${BASE_URL}/${url}`;
  };

  useEffect(() => {
    const fetchReference = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${BASE_URL}/backend/api.php?action=getReferenceBySlug&slug=${slug}`);
        const data = await response.json();
        
        let ref = null;
        if (data && data.status === 'success' && data.data) {
          ref = data.data;
        } else if (data && typeof data === 'object' && !Array.isArray(data) && (data.id || data.name)) {
          ref = data;
        }

        if (ref) {
          setReference(ref);
          
          // Fetch category if exists
          if (ref.category_id) {
            const catRes = await fetch(`${BASE_URL}/backend/api.php?action=getReferenceCategories`);
            const catData = await catRes.json();
            const cats = Array.isArray(catData) ? catData : (catData?.data || []);
            const cat = cats.find(c => c.id == ref.category_id);
            setCategory(cat || null);
          }
          
          // Fetch related references
          const relatedRes = await fetch(API_URLS.REFERENCES);
          const relatedData = await relatedRes.json();
          if (Array.isArray(relatedData)) {
            const related = relatedData
              .filter(r => r.id !== ref.id && r.category_id === ref.category_id)
              .slice(0, 6);
            setRelatedReferences(related);
          } else if (relatedData?.status === 'success' && Array.isArray(relatedData.data)) {
            const related = relatedData.data
              .filter(r => r.id !== ref.id && r.category_id === ref.category_id)
              .slice(0, 6);
            setRelatedReferences(related);
          }
        } else {
          navigate('/404');
        }
      } catch (error) {
        console.error('Error fetching reference:', error);
        navigate('/404');
      } finally {
        setLoading(false);
      }
    };

    fetchReference();
  }, [slug, navigate, BASE_URL]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Referans yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!reference) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-purple-50">
      {/* Header */}
      <section className="relative bg-gradient-to-br from-blue-600 via-purple-600 to-blue-700 py-12">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjEiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-20"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <Button
            variant="ghost"
            onClick={() => navigate('/referanslar')}
            className="text-white hover:bg-white/20 mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Referanslara Dön
          </Button>
          
          <div className="flex items-center gap-3 mb-4">
            {category && (
              <Badge className="bg-white/20 text-white border-white/30">
                {category.name}
              </Badge>
            )}
            <Badge className="bg-green-500/90 text-white">
              <CheckCircle2 className="w-3 h-3 mr-1" />
              Tamamlandı
            </Badge>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-extrabold text-white leading-tight">
            {reference.name}
          </h1>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Logo */}
              <div className="overflow-hidden">
                <div className="aspect-video flex items-center justify-center">
                  {reference.logo ? (
                    <img
                      src={normalizeUrl(reference.logo)}
                      alt={reference.name}
                      className="w-full h-full object-contain"
                      onError={(e) => { e.target.src = 'https://placehold.co/800x400/e0e7ff/6366f1?text=' + encodeURIComponent(reference.name); }}
                    />
                  ) : (
                    <Building2 className="w-32 h-32 text-gray-400" />
                  )}
                </div>
              </div>

              {/* Description */}
              {reference.description && (
                <Card>
                  <CardContent className="p-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Hakkında</h2>
                    <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                      {reference.description}
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Quick Info Card */}
              <Card>
                <CardContent className="p-6 space-y-4">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Proje Bilgileri</h3>
                  
                  {category && (
                    <div className="flex items-start space-x-3">
                      <Tag className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Kategori</p>
                        <p className="font-semibold text-gray-900">{category.name}</p>
                      </div>
                    </div>
                  )}
                  
                  {reference.created_at && (
                    <div className="flex items-start space-x-3">
                      <Calendar className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Eklenme Tarihi</p>
                        <p className="font-semibold text-gray-900">
                          {new Date(reference.created_at).toLocaleDateString('tr-TR')}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {reference.website && (
                    <div className="pt-4 border-t">
                      <Button
                        onClick={() => window.open(reference.website, '_blank')}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        <Globe className="w-4 h-4 mr-2" />
                        Web Sitesini Ziyaret Et
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* CTA Card */}
              <Card className="bg-gradient-to-br from-blue-600 to-purple-600 text-white">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-3">Sizin için de çalışalım!</h3>
                  <p className="text-blue-100 mb-4 text-sm">
                    Projeniz için profesyonel çözümler sunuyoruz.
                  </p>
                  <Button
                    onClick={() => navigate('/iletisim')}
                    className="w-full bg-white text-blue-600 hover:bg-gray-100"
                  >
                    Hemen İletişime Geçin
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Related References */}
          {relatedReferences.length > 0 && (
            <div className="mt-16">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-3xl font-bold text-gray-900">Benzer Referanslar</h2>
                <Link to="/referanslar">
                  <Button variant="outline">
                    Tümünü Gör
                    <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
                  </Button>
                </Link>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {relatedReferences.map((item) => (
                  <Link
                    key={item.id}
                    to={`/referanslar/${item.slug || item.id}`}
                    className="group block overflow-hidden transition-all duration-300"
                  >
                    <div className="aspect-square flex items-center justify-center p-4">
                      {item.logo ? (
                        <img
                          src={normalizeUrl(item.logo)}
                          alt={item.name}
                          className="max-w-full max-h-full object-contain transition-transform duration-300 group-hover:scale-110 filter grayscale group-hover:grayscale-0"
                          onError={(e) => { e.target.src = 'https://placehold.co/200x200/e0e7ff/6366f1?text=' + encodeURIComponent(item.name); }}
                        />
                      ) : (
                        <Building2 className="w-8 h-8 text-gray-400 group-hover:text-blue-600 transition-colors" />
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

